import {
  Box,
  CircularProgress,
  IconButton,
  List,
  Paper,
  Tooltip,
  Typography
} from '@material-ui/core'
import { Layers2, Paperclip, Zap } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { InputFieldText } from '../InputFieldText'
import { useStyles } from './style'

const QuickMessagesDropdown = ({
  quickMessages,
  onSelectMessage,
  onClose,
  searchTerm = '',
  onSearchChange,
  isLoading = false,
  isTypingSlash = false
}) => {
  const classes = useStyles()
  const [filteredMessages, setFilteredMessages] = useState(quickMessages)
  const dropdownRef = useRef(null)

  useEffect(() => {
    const handleKeyDown = event => {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    const handleClickOutside = event => {
      {
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(event.target)
        ) {
          onClose()
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [onClose])

  useEffect(() => {
    if (!searchTerm) {
      setFilteredMessages(quickMessages)
    } else {
      const filtered = quickMessages.filter(
        message =>
          message.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
          message.value.toLowerCase().includes(searchTerm.toLowerCase())
      )
      setFilteredMessages(filtered)
    }
  }, [searchTerm, quickMessages])

  const handleSelectMessage = message => {
    onSelectMessage(message, true)
    onClose()
  }

  // const formatMessagePreview = text => {
  //   if (isString(text) && text.length > 40) {
  //     return text.substring(0, 40) + '...'
  //   }
  //   return text
  // }

  const extractShortcode = label => {
    const match = label.match(/^\/(.+?) -/)
    return match ? match[1] : label
  }

  return (
    <Paper
      className={classes.quickMessagesContainer}
      elevation={3}
      ref={dropdownRef}
    >
      <div className={classes.searchContainer}>
        <InputFieldText
          placeholder="Pesquisar..."
          className={classes.searchInput}
          value={searchTerm}
          onChange={e => onSearchChange(e.target.value)}
          autoFocus={!isLoading}
          disabled={isLoading}
          onKeyDown={e => {
            if (e.key === 'Escape') {
              e.preventDefault()
              onClose()
            }
          }}
        />
      </div>

      <List className={classes.messagesList}>
        {isLoading ? (
          <div className={classes.loadingState}>
            <CircularProgress size={24} />
            <Typography variant="body2" style={{ marginTop: 8 }}>
              Carregando mensagens...
            </Typography>
          </div>
        ) : filteredMessages.length > 0 ? (
          filteredMessages.map((message, index) => (
            <Tooltip
              title={message.value}
              placement="right"
              arrow
              key={index}
              PopperProps={{
                style: { zIndex: 10000 }
              }}
              classes={{
                tooltip: classes.customTooltip,
                arrow: classes.customArrow
              }}
            >
              <div
                className={classes.messageItem}
                onClick={() => handleSelectMessage(message)}
              >
                <div className={classes.messageIcon}>
                  <Layers2 size={18} />
                </div>
                <div className={classes.messageText}>
                  <div className={classes.messageTextPrimary}>
                    {extractShortcode(message.label)}
                  </div>
                </div>
                {message.mediaPath && (
                  <Paperclip size={14} style={{ opacity: 0.6, flexShrink: 0 }} />
                )}
              </div>
            </Tooltip>
          ))
        ) : (
          <div className={classes.emptyState}>
            <Typography variant="body2">
              Nenhuma mensagem rápida encontrada
            </Typography>
          </div>
        )}
      </List>
    </Paper>
  )
}

const QuickMessageOptions = ({
  disabled,
  quickMessages,
  onSelectMessage,
  showQuickMessages,
  setShowQuickMessages,
  loadQuickMessages,
  isTypingSlash = false,
  inputMessage = '',
  hideIcon = false
}) => {
  const classes = useStyles()
  const [searchTerm, setSearchTerm] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const getFilteredMessages = () => {
    if (isTypingSlash && inputMessage.includes('/')) {
      const slashPart = inputMessage.substring(
        inputMessage.lastIndexOf('/') + 1
      )
      if (slashPart) {
        return quickMessages.filter(
          message =>
            message.shortcode.toLowerCase().includes(slashPart.toLowerCase()) ||
            message.label.toLowerCase().includes(slashPart.toLowerCase())
        )
      }
    }

    if (searchTerm) {
      return quickMessages.filter(
        message =>
          message.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
          message.value.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    return quickMessages
  }

  const handleToggleQuickMessages = async () => {
    if (!showQuickMessages) {
      if (quickMessages.length === 0) {
        setIsLoading(true)
        try {
          await loadQuickMessages()
        } catch (error) {
          console.error('Erro ao carregar mensagens:', error)
        } finally {
          setIsLoading(false)
        }
      }
      setShowQuickMessages(true)
      setSearchTerm('')
    } else {
      setShowQuickMessages(false)
      setSearchTerm('')
    }
  }

  return (
    <Box position="relative">
      {!hideIcon && (
        <Tooltip title="Mensagens rápidas" placement="right" arrow>
          <IconButton
            aria-label="quickMessagePicker"
            component="span"
            disabled={disabled}
            onClick={handleToggleQuickMessages}
            className={classes.actionsIconButton}
          >
            <Zap className={classes.buttonActionQuickMessage} size={24} />
          </IconButton>
        </Tooltip>
      )}

      {showQuickMessages && (
        <QuickMessagesDropdown
          quickMessages={getFilteredMessages()}
          onSelectMessage={onSelectMessage}
          onClose={() => setShowQuickMessages(false)}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          isLoading={isLoading}
          isTypingSlash={isTypingSlash}
        />
      )}
    </Box>
  )
}

export default QuickMessageOptions
